import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Shield, Loader2, LogIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const TOKEN_KEY = 'finance_token';
const USER_KEY = 'finance_user';

export function getFinanceToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function setFinanceSession(token, user) {
  try {
    localStorage.setItem(TOKEN_KEY, token || '');
    localStorage.setItem(USER_KEY, JSON.stringify(user || {}));
  } catch {}
}
export function clearFinanceSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {}
}
export function getFinanceUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
}

export default function FinanceAuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in, jump to dashboard
  useEffect(() => {
    const t = getFinanceToken();
    if (t) {
      api.get('/api/finance/auth/me', { headers: { Authorization: `Bearer ${t}` } })
        .then((r) => { if (r.data?.ok) navigate('/finance', { replace: true }); })
        .catch(() => clearFinanceSession());
    }
  }, [navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) { toast.error('Enter email & password'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/finance/auth/login', { email, password });
      if (res.data?.token) {
        setFinanceSession(res.data.token, res.data.user);
        toast.success(`Welcome, ${res.data.user?.name || res.data.user?.email}`);
        navigate('/finance', { replace: true });
      } else {
        toast.error('Login failed');
      }
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0B0E14' }}>
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#131822] p-8 shadow-2xl"
        data-testid="finance-auth-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-blue-500/20 text-blue-400">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <div className="text-white text-xl font-bold">Finance Portal</div>
            <div className="text-xs text-gray-400">Restricted to finance staff only</div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="finance@example.com"
              className="bg-[#0B0E14] border-white/10 text-white"
              data-testid="finance-login-email"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0B0E14] border-white/10 text-white"
              data-testid="finance-login-password"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full mt-2"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff' }}
            data-testid="finance-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
            Sign In
          </Button>
          <p className="text-xs text-gray-500 text-center mt-3">
            Need access? Ask the main administrator to create your account.
          </p>
        </div>
      </form>
    </div>
  );
}
